import React, { FC, KeyboardEvent, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

export type AutocompleteOption = { value: string; label: string };
export type AutocompleteSection = { title: string; options: AutocompleteOption[] };

type AutocompleteProps = {
  label: string;
  // Hides the label visually but keeps it as the field's accessible name.
  labelHidden?: boolean;
  placeholder?: string;
  sections: AutocompleteSection[];
  // Text shown in the field before the user types, e.g. the current choice.
  defaultValue?: string;
  onSelect: (option: AutocompleteOption) => void;
};

const filterSections = (sections: AutocompleteSection[], query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return sections;
  return sections
    .map((section) => ({
      ...section,
      options: section.options.filter((option) => option.label.toLowerCase().includes(needle)),
    }))
    .filter((section) => section.options.length > 0);
};

// Replaces Polaris React's Autocomplete, which has no Polaris web component
// equivalent: a text field with a filtered, grouped listbox (ARIA 1.2 combobox
// pattern). It uses a native input because aria-controls and
// aria-activedescendant can't reference elements across s-text-field's shadow root.
// The listbox is a popover in the top layer, so containers with overflow (like
// s-table's scroll box) don't clip it.
const Autocomplete: FC<AutocompleteProps> = ({
  label,
  labelHidden = false,
  placeholder,
  sections,
  defaultValue = '',
  onSelect,
}) => {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const [text, setText] = useState(defaultValue);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const visible = useMemo(() => filterSections(sections, query), [sections, query]);
  const options = useMemo(() => visible.flatMap((section) => section.options), [visible]);
  const optionId = (index: number) => `${id}-option-${index}`;
  const expanded = open && options.length > 0;

  useLayoutEffect(() => {
    if (!expanded) return;
    const update = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) });
    };
    update();
    // Set here because styled-components 5 doesn't pass the popover prop through.
    // Only with native support: the Polaris bundle polyfills showPopover (e.g. in
    // jsdom), where the list would then stay hidden. Without it, the list renders in place.
    const listbox = listboxRef.current;
    if (listbox && typeof CSS !== 'undefined' && CSS.supports?.('selector(:popover-open)')) {
      listbox.setAttribute('popover', 'manual');
      listbox.showPopover();
    }
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [expanded]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const select = (option: AutocompleteOption) => {
    setText(option.label);
    setQuery('');
    close();
    onSelect(option);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!options.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setOpen(true);
      setActive((current) => (current + step + options.length) % options.length);
    } else if (event.key === 'Enter' && open && active >= 0) {
      event.preventDefault();
      select(options[active]);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  let index = -1;
  return (
    <Wrapper>
      <Label htmlFor={`${id}-input`} $hidden={labelHidden}>{label}</Label>
      <Input
        ref={inputRef}
        id={`${id}-input`}
        type='text'
        role='combobox'
        autoComplete='off'
        aria-autocomplete='list'
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        placeholder={placeholder}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setQuery(event.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      {expanded && <Listbox
        ref={listboxRef}
        id={listboxId}
        role='listbox'
        aria-label={label}
        style={position ?? undefined}
      >
        {visible.map((section, sectionIndex) => (
          <li key={section.title} role='presentation'>
            <SectionTitle id={`${id}-section-${sectionIndex}`} role='presentation'>{section.title}</SectionTitle>
            <ul role='group' aria-labelledby={`${id}-section-${sectionIndex}`}>
              {section.options.map((option) => {
                index += 1;
                const optionIndex = index;
                return (
                  <Option
                    key={option.value}
                    id={optionId(optionIndex)}
                    role='option'
                    aria-selected={optionIndex === active}
                    $active={optionIndex === active}
                    // Keeps focus in the input, so blur doesn't close the list before the click.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => select(option)}
                  >
                    {option.label}
                  </Option>
                );
              })}
            </ul>
          </li>
        ))}
      </Listbox>}
    </Wrapper>
  );
};

export default Autocomplete;

const Wrapper = styled.div`
  position: relative;
  min-width: 0;
`;

const Label = styled.label<{ $hidden: boolean }>`
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  ${(props) => props.$hidden && `
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
  `}
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 32px;
  padding: 6px 12px;
  border: 1px solid #8a8a8a;
  border-radius: 8px;
  background: #fdfdfd;
  color: #303030;
  font: inherit;
  font-size: 13px;

  &:hover {
    border-color: #616161;
  }

  &:focus-visible {
    outline: 2px solid #005bd3;
    outline-offset: 1px;
  }
`;

const Listbox = styled.ul`
  position: fixed;
  inset: auto;
  max-height: 260px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #fff;
  border-radius: 12px;
  border: 0;
  color: inherit;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.06);

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
`;

const SectionTitle = styled.div`
  padding: 6px 8px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
`;

const Option = styled.li<{ $active: boolean }>`
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  background: ${(props) => (props.$active ? '#f1f1f1' : 'transparent')};

  &:hover {
    background: #f1f1f1;
  }
`;
